import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import {
    Box, Typography, Paper, Grid, Container, Button,
    Table, TableBody, TableCell, TableContainer, TableHead,
    TableRow, Chip, TextField, Avatar, Stack,
    Breadcrumbs, Link, CircularProgress, Snackbar, Tooltip, IconButton, Fade
} from '@mui/material';
import {
    ArrowBack as ArrowBackIcon,
    Person as PersonIcon,
    CheckCircle as CheckCircleIcon,
    Cancel as CancelIcon,
    Event as EventIcon,
    School as SchoolIcon,
    CalendarToday as CalendarTodayIcon,
    People as PeopleIcon,
    Refresh as RefreshIcon,
    ChevronRight as ChevronRightIcon,
    ErrorOutline as ErrorOutlineIcon
} from '@mui/icons-material';
import axios from 'axios';

// Animations
const fadeIn = keyframes`from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); }`;

// Styled Components
const PageWrapper = styled(Box)`
    background-color: #f0f4f8;
    min-height: 100vh;
    padding-bottom: 60px;
    animation: ${fadeIn} 0.5s ease-out;
`;

const PremiumHeader = styled(Box)`
    background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%);
    padding: 80px 0 120px;
    color: white;
    position: relative;
    overflow: hidden;
    
    &::after {
        content: '';
        position: absolute;
        top: 0;
        right: 0;
        width: 400px;
        height: 400px;
        background: radial-gradient(circle, rgba(99, 102, 241, 0.15) 0%, transparent 70%);
        border-radius: 50%;
        transform: translate(30%, -30%);
    }
`;

const ContentContainer = styled(Container)`
    margin-top: -80px;
    position: relative;
    z-index: 2;
`;

const HeroCard = styled(Paper)`
    border-radius: 32px !important;
    background: rgba(255, 255, 255, 0.9) !important;
    backdrop-filter: blur(20px) !important;
    border: 1px solid rgba(255, 255, 255, 0.5) !important;
    padding: 40px;
    box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.1) !important;
    margin-bottom: 40px;
`;

const PremiumCard = styled(Paper)`
    border-radius: 24px !important;
    border: 1px solid #e2e8f0 !important;
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.03) !important;
    overflow: hidden;
    background: white !important;
`;

const ClassCard = styled(Paper)`
    border-radius: 24px !important;
    border: 1px solid #e2e8f0 !important;
    background: white !important;
    padding: 24px;
    cursor: pointer;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05) !important;
    display: flex;
    align-items: center;
    justify-content: space-between;

    &:hover {
        transform: translateY(-8px);
        box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04) !important;
        border-color: #6366f1 !important;
    }
`;

const TableWrapper = styled(Paper)`
    border-radius: 32px !important;
    overflow: hidden;
    border: 1px solid #e2e8f0 !important;
    background: white !important;
    box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.05) !important;
`;

const StudentRow = styled(TableRow)`
    &:hover {
        background-color: #f8fafc;
    }
    
    td {
        padding: 16px 20px;
        border-bottom: 1px solid #f1f5f9;
    }
`;

const StudentAvatar = styled(Avatar)`
    width: 48px !important;
    height: 48px !important;
    border-radius: 16px !important;
    background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%) !important;
    box-shadow: 0 8px 16px rgba(59, 130, 246, 0.2) !important;
`;

const StatusBadge = styled(Chip)`
    height: 32px !important;
    border-radius: 10px !important;
    font-weight: 800 !important;
    font-size: 0.75rem !important;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    border: none !important;
`;

const ActionBtn = styled(Button)`
    border-radius: 14px !important;
    padding: 8px 16px !important;
    font-weight: 800 !important;
    text-transform: none !important;
`;

const StudentAttendance = () => {
    const dispatch = useDispatch();
    const { currentUser } = useSelector((state) => state.user);

    const [classes, setClasses] = useState([]);
    const [selectedClass, setSelectedClass] = useState(null);
    const [students, setStudents] = useState([]);
    const [attendanceData, setAttendanceData] = useState({});
    const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
    const [loading, setLoading] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    const schoolId = currentUser?._id;
    const location = useLocation();

    // Check for auto-select class from navigation state
    useEffect(() => {
        if (location.state && location.state.autoSelectClass) {
            setSelectedClass(location.state.autoSelectClass);
        }
    }, [location.state]);

    // Fetch all classes on component mount
    useEffect(() => {
        if (schoolId) {
            fetchClasses();
        }
    }, [schoolId]);

    // Fetch students and attendance when class and date are selected
    useEffect(() => {
        if (selectedClass && selectedDate) {
            fetchStudentsAndAttendance();
        }
    }, [selectedClass, selectedDate]);

    const fetchClasses = async () => {
        setLoading(true);
        try {
            const result = await axios.get(`${process.env.REACT_APP_BASE_URL}/SclassList/${schoolId}`);
            if (result.data && result.data.length > 0) {
                setClasses(result.data);
            } else {
                setSnackbar({ open: true, message: 'No classes found', severity: 'info' });
            }
        } catch (error) {
            console.error('Error fetching classes:', error);
            setSnackbar({ open: true, message: 'Error loading classes', severity: 'error' });
        }
        setLoading(false);
    };

    const fetchStudentsAndAttendance = async () => {
        setLoading(true);
        try {
            // Fetch students for the selected class
            const studentsResult = await axios.get(`${process.env.REACT_APP_BASE_URL}/Sclass/Students/${selectedClass._id}`);
            
            if (studentsResult.data && studentsResult.data.length > 0) {
                const studentData = studentsResult.data.map(student => ({
                    ...student,
                    _id: student._id,
                    name: student.name,
                    rollNum: student.rollNum
                }));
                
                setStudents(studentData);
                await fetchAttendanceForDate(studentData);
            } else {
                setStudents([]);
                setAttendanceData({});
                setSnackbar({ open: true, message: 'No students found in this class', severity: 'info' });
            }
        } catch (error) {
            console.error('Error fetching students:', error);
            setSnackbar({ open: true, message: 'Error loading students', severity: 'error' });
        }
        setLoading(false);
    };

    const fetchAttendanceForDate = async (studentList) => {
        try {
            const attendanceMap = {};
            // Fetch attendance for each student
            for (const student of studentList) {
                try {
                    const studentResult = await axios.get(`${process.env.REACT_APP_BASE_URL}/Student/${student._id}`);
                    if (studentResult.data && studentResult.data.attendance) {
                        const attendanceRecord = studentResult.data.attendance.find(a => {
                            const attendanceDate = new Date(a.date).toISOString().split('T')[0];
                            return attendanceDate === selectedDate;
                        });

                        if (attendanceRecord) {
                            attendanceMap[student._id] = {
                                status: attendanceRecord.status,
                                subName: attendanceRecord.subName
                            };
                        }
                    }
                } catch (err) {
                    console.error(`Error fetching attendance for student ${student._id}:`, err);
                }
            }
            setAttendanceData(attendanceMap);
        } catch (error) {
            console.error('Error fetching attendance:', error);
        }
    };

    const handleClassClick = (cls) => {
        setSelectedClass(cls);
        setStudents([]);
        setAttendanceData({});
    };

    const handleBackToClasses = () => {
        setSelectedClass(null);
        setStudents([]);
        setAttendanceData({});
    };

    const handleCloseSnackbar = () => {
        setSnackbar({ ...snackbar, open: false });
    };

    const renderClassesView = () => (
        <ContentContainer maxWidth="xl">
            <Typography variant="h4" sx={{ fontWeight: 900, color: '#1e293b', mb: 4, display: 'flex', alignItems: 'center', gap: 2 }}>
                <SchoolIcon sx={{ color: '#6366f1' }} fontSize="large" /> Select a Class
            </Typography>

            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 8 }}>
                    <CircularProgress size={60} thickness={4} />
                </Box>
            ) : classes.length === 0 ? (
                <PremiumCard sx={{ p: 8, textAlign: 'center' }}>
                    <ErrorOutlineIcon sx={{ fontSize: 80, color: '#94a3b8', mb: 2 }} />
                    <Typography variant="h5" sx={{ fontWeight: 800, color: '#475569', mb: 1 }}>
                        No Classes Found
                    </Typography>
                    <Typography variant="body1" sx={{ color: '#64748b' }}>
                        Please create classes to manage student attendance.
                    </Typography>
                </PremiumCard>
            ) : (
                <Grid container spacing={4}>
                    {classes.map((cls) => (
                        <Grid item xs={12} sm={6} md={4} key={cls._id}>
                            <ClassCard onClick={() => handleClassClick(cls)}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                                    <Avatar sx={{ width: 64, height: 64, bgcolor: '#f1f5f9', color: '#6366f1' }}>
                                        <SchoolIcon fontSize="large" />
                                    </Avatar>
                                    <Box>
                                        <Typography variant="h5" sx={{ fontWeight: 900, color: '#1e293b' }}>
                                            Class {cls.sclassName}
                                        </Typography>
                                        <Typography variant="body2" sx={{ color: '#64748b', fontWeight: 600 }}>
                                            View Attendance
                                        </Typography>
                                    </Box>
                                </Box>
                                <IconButton sx={{ bgcolor: '#f8fafc', '&:hover': { bgcolor: '#e2e8f0' } }}>
                                    <ChevronRightIcon />
                                </IconButton>
                            </ClassCard>
                        </Grid>
                    ))}
                </Grid>
            )}
        </ContentContainer>
    );

    const renderStudentsView = () => {
        const presentCount = Object.values(attendanceData).filter(a => a.status === 'Present').length;
        const absentCount = Object.values(attendanceData).filter(a => a.status === 'Absent').length;
        const notMarkedCount = students.length - presentCount - absentCount;

        return (
            <ContentContainer maxWidth="xl">
                <HeroCard>
                    <Grid container spacing={4} alignItems="center">
                        <Grid item xs={12} md={4}>
                            <Typography variant="h6" sx={{ fontWeight: 900, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                                <EventIcon color="primary" /> Select Date
                            </Typography>
                            <TextField
                                fullWidth
                                type="date"
                                value={selectedDate}
                                onChange={(e) => setSelectedDate(e.target.value)}
                                InputProps={{ sx: { borderRadius: '18px', fontWeight: 800, py: 0.5, fontSize: '1.1rem' } }}
                            />
                        </Grid>
                        <Grid item xs={12} md={8}>
                            <Grid container spacing={3}>
                                {[
                                    { label: 'Total Students', value: students.length, icon: <PeopleIcon />, color: '#6366f1' },
                                    { label: 'Present Today', value: presentCount, icon: <CheckCircleIcon />, color: '#10b981' },
                                    { label: 'Absent', value: absentCount, icon: <CancelIcon />, color: '#f43f5e' },
                                    { label: 'Not Marked', value: notMarkedCount, icon: <RefreshIcon />, color: '#94a3b8' }
                                ].map((stat, i) => (
                                    <Grid item xs={6} sm={3} key={i}>
                                        <Box sx={{ textAlign: 'center' }}>
                                            <Typography variant="h4" sx={{ fontWeight: 900, color: stat.color }}>{stat.value}</Typography>
                                            <Typography variant="caption" sx={{ fontWeight: 700, color: '#64748b', textTransform: 'uppercase' }}>{stat.label}</Typography>
                                        </Box>
                                    </Grid>
                                ))}
                            </Grid>
                        </Grid>
                    </Grid>
                </HeroCard>

                <TableWrapper>
                    <TableContainer>
                        <Table>
                            <TableHead sx={{ bgcolor: '#f8fafc' }}>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 800, color: '#475569', py: 3, width: '10%' }}>ROLL</TableCell>
                                    <TableCell sx={{ fontWeight: 800, color: '#475569', width: '35%' }}>STUDENT DETAILS</TableCell>
                                    <TableCell sx={{ fontWeight: 800, color: '#475569', width: '25%' }}>SUBJECT</TableCell>
                                    <TableCell align="center" sx={{ fontWeight: 800, color: '#475569', width: '30%' }}>STATUS</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {loading ? (
                                    <TableRow>
                                        <TableCell colSpan={4} align="center" sx={{ py: 10 }}>
                                            <CircularProgress size={60} thickness={4} />
                                            <Typography sx={{ mt: 2, fontWeight: 600, color: '#94a3b8' }}>Loading Data...</Typography>
                                        </TableCell>
                                    </TableRow>
                                ) : students.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={4} align="center" sx={{ py: 10 }}>
                                            <PeopleIcon sx={{ fontSize: 80, color: '#cbd5e1', mb: 2 }} />
                                            <Typography variant="h6" sx={{ fontWeight: 700, color: '#64748b' }}>No Students Found</Typography>
                                        </TableCell>
                                    </TableRow>
                                ) : students.map((student) => {
                                    const attendance = attendanceData[student._id];
                                    const status = attendance?.status || 'Not Marked';
                                    
                                    return (
                                        <StudentRow key={student._id}>
                                            <TableCell>
                                                <Typography sx={{ fontWeight: 900, color: '#64748b' }}>
                                                    {student.rollNum || '-'}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Stack direction="row" spacing={2} alignItems="center">
                                                    <StudentAvatar>{student.name.charAt(0)}</StudentAvatar>
                                                    <Box>
                                                        <Typography sx={{ fontWeight: 900, color: '#1e293b' }}>{student.name}</Typography>
                                                        <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600 }}>ID: {student._id.slice(-6)}</Typography>
                                                    </Box>
                                                </Stack>
                                            </TableCell>
                                            <TableCell>
                                                {attendance?.subName ? (
                                                    <Chip label={attendance.subName} size="small" sx={{ fontWeight: 700, bgcolor: '#f1f5f9', color: '#475569' }} />
                                                ) : (
                                                    <Typography variant="body2" sx={{ color: '#94a3b8', fontStyle: 'italic' }}>Not Assigned</Typography>
                                                )}
                                            </TableCell>
                                            <TableCell align="center">
                                                <StatusBadge
                                                    label={status}
                                                    icon={status === 'Present' ? <CheckCircleIcon /> : status === 'Absent' ? <CancelIcon /> : null}
                                                    sx={{
                                                        bgcolor: status === 'Present' ? '#dcfce7' : status === 'Absent' ? '#fee2e2' : '#f1f5f9',
                                                        color: status === 'Present' ? '#15803d' : status === 'Absent' ? '#b91c1c' : '#475569'
                                                    }}
                                                />
                                            </TableCell>
                                        </StudentRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </TableWrapper>
            </ContentContainer>
        );
    };

    return (
        <PageWrapper>
            <PremiumHeader>
                <Container maxWidth="xl">
                    <Fade in timeout={800}>
                        <Box>
                            <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
                                {selectedClass && (
                                    <Tooltip title="Back to Classes">
                                        <IconButton onClick={handleBackToClasses} sx={{ color: 'white', bgcolor: 'rgba(255,255,255,0.1)' }}>
                                            <ArrowBackIcon />  
                                        </IconButton>
                                    </Tooltip>
                                )}
                                <Typography variant="h2" sx={{ fontWeight: 900, mb: 1, letterSpacing: '-0.03em' }}>
                                    {selectedClass ? `Class ${selectedClass.sclassName} Attendance` : 'Student Attendance'}
                                </Typography>
                            </Stack>
                            <Typography variant="h6" sx={{ opacity: 0.6, maxWidth: 600, fontWeight: 400 }}>
                                View real-time student attendance records and track daily presence.
                            </Typography>
                        </Box>
                    </Fade>
                </Container>
            </PremiumHeader>

            {selectedClass ? renderStudentsView() : renderClassesView()}

            <Snackbar 
                open={snackbar.open} 
                autoHideDuration={6000} 
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Paper sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 1.5, borderRadius: 2, boxShadow: 3, bgcolor: snackbar.severity === 'error' ? '#fee2e2' : snackbar.severity === 'success' ? '#dcfce7' : '#f1f5f9', color: snackbar.severity === 'error' ? '#991b1b' : snackbar.severity === 'success' ? '#166534' : '#334155' }}>
                    {snackbar.severity === 'error' && <CancelIcon />}
                    {snackbar.severity === 'success' && <CheckCircleIcon />}
                    {snackbar.severity === 'info' && <EventIcon />}
                    <Typography variant="body1" sx={{ fontWeight: 700 }}>{snackbar.message}</Typography>
                </Paper>
            </Snackbar>
        </PageWrapper>
    );
};

export default StudentAttendance;
